import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useApiSubmit from '@/hooks/useApiSubmit';
import questionsAndAnswers, {
  EditRotatingQuestionProps,
  CreateRotatingQuestionProps,
} from '@/services/questionsAndAnswers';
import {
  AnswerTypes,
  QuestionFrequency,
  RotatingQuestionInputData,
  Question,
  QuestionOption,
} from '@/types/api';
import { Paths } from '@/types/routes';
import ManageRotatingQuestionPage from './ManageRotatingQuestion.component';

type StateReceived = {
  question: Question;
};

const initialObject: RotatingQuestionInputData = {
  id: '0',
  answerType: AnswerTypes.STRING,
  label: '',
  description: '',
  questionFrequency: QuestionFrequency.QUESTION_QUEUED,
  isRequired: true,
  isActive: true,
};

const ManageRotatingQuestionContainer = function (): JSX.Element {
  const { state } = useLocation();
  const navigate = useNavigate();

  const editRotatingQuestionMutation = useApiSubmit<Question, EditRotatingQuestionProps>(
    questionsAndAnswers.editRotatingQuestion
  );
  const createRotatingQuestionMutation = useApiSubmit<Question, CreateRotatingQuestionProps>(
    questionsAndAnswers.createRotatingQuestion
  );

  const [isEditManagement, setIsEditManagement] = useState(false);
  const [questionData, setQuestionData] = useState<RotatingQuestionInputData>(initialObject);
  const [isDateInputOpen, setIsDateInputOpen] = useState(false);

  const [isOptionsInputOpen, setIsOptionsInputOpen] = useState(false);
  const [optionsQuantity, setOptionsQuantity] = useState(0);

  useEffect(() => {
    if (state) {
      const { question } = state as StateReceived;

      const questionToEditObject: RotatingQuestionInputData = {
        id: question.id,
        answerType: question.answerType,
        label: question.label,
        description: question.description,
        questionFrequency: question.questionFrequency,
        isRequired: question.isRequired,
        isActive: question.isActive,
        scheduledAt: question.scheduledAt,
        options: question.options,
      };

      setQuestionData(questionToEditObject);
      setIsDateInputOpen(!!question.scheduledAt);
      setIsOptionsInputOpen(!!question.options?.length);
      setIsEditManagement(true);
    }
  }, [state]);

  const handleChangeData = (input: keyof RotatingQuestionInputData, value: any): void => {
    setQuestionData((currentObject) => {
      if (input === 'questionFrequency') {
        if (value === QuestionFrequency.SCHEDULED) {
          setIsDateInputOpen(true);
        } else {
          setIsDateInputOpen(false);
          Object.assign(currentObject, { questionDate: undefined });
        }
      }

      if (input === 'answerType') {
        if (value === AnswerTypes.NUMBER) {
          setIsOptionsInputOpen(true);
          setOptionsQuantity(5);
        } else {
          setIsOptionsInputOpen(false);
          setOptionsQuantity(0);
          Object.assign(currentObject, { options: undefined });
        }
      }

      return {
        ...currentObject,
        [input]: value,
      };
    });
  };

  const handleChangeOptionsQuantity = (event: Event, newValue: number | number[]): void => {
    setOptionsQuantity(newValue as number);
  };

  const handleManageQuestion = useCallback(async (): Promise<void> => {
    if (questionData) {
      if (isEditManagement) {
        editRotatingQuestionMutation.mutate({ question: questionData });
        return;
      }

      delete questionData.id;

      createRotatingQuestionMutation.mutate({ question: questionData });
    }
  }, [
    createRotatingQuestionMutation,
    editRotatingQuestionMutation,
    isEditManagement,
    questionData,
  ]);

  const getOptionsArray = useCallback((quantity: number): Array<Pick<QuestionOption, 'label'>> => {
    const options: Array<Pick<QuestionOption, 'label'>> = [];

    for (let optionIndex = 1; optionIndex <= quantity; optionIndex += 1) {
      const optionObject = {
        label: String(optionIndex),
      };

      options.push(optionObject);
    }

    return options;
  }, []);

  useEffect(() => {
    if (optionsQuantity !== 0) {
      const options: Array<Pick<QuestionOption, 'label'>> = getOptionsArray(optionsQuantity);

      handleChangeData('options', options);
    }
  }, [getOptionsArray, optionsQuantity]);

  useEffect(() => {
    if (editRotatingQuestionMutation.data) {
      setQuestionData(editRotatingQuestionMutation.data);
      navigate(Paths.ROTATING_QUESTIONS);
    }
  }, [editRotatingQuestionMutation.data, navigate]);

  useEffect(() => {
    if (createRotatingQuestionMutation.data) {
      setQuestionData(createRotatingQuestionMutation.data);
      navigate(Paths.ROTATING_QUESTIONS);
    }
  }, [createRotatingQuestionMutation.data, navigate]);

  return (
    <ManageRotatingQuestionPage
      isEditManagement={isEditManagement}
      isDateInputOpen={isDateInputOpen}
      isOptionsInputOpen={isOptionsInputOpen}
      questionData={questionData}
      handleChangeData={handleChangeData}
      handleChangeOptionsQuantity={handleChangeOptionsQuantity}
      handleManageQuestion={handleManageQuestion}
    />
  );
};

export default ManageRotatingQuestionContainer;
